import { Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  createCompetitorProfile,
  updateCompetitorProfile,
} from "../api/company-knowledge";
import { useLoading, useToaster } from "../hooks";
import {
  BusinessFeature,
  CompetitorProfile,
  CustomerProfileSubset,
} from "../types/company-knowledge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import { toaster } from "./toaster";

// A competitor passes `competitor` to edit an existing one, or null to
// create. The ICP subsets come from the parent page, which loads them
// alongside the competitors for its "Applicable ICP subsets" column.

const competitorProfileSchema = z.object({
  name: z.string().trim().min(1, "A name is required."),
  "competitive-niche": z.string(),
  "website-url": z
    .string()
    .refine(
      (value) => value === "" || /^https?:\/\//.test(value),
      "Enter a valid URL (starting with http:// or https://), or leave it blank.",
    ),
  "competing-features": z.string(),
  differentiators: z.string(),
  "relative-strengths": z.string(),
  "relative-weaknesses": z.string(),
  "subset-ids": z.array(z.number()),
  "related-feature-ids": z.array(z.number()),
});

type CompetitorProfileFormData = z.infer<typeof competitorProfileSchema>;

interface CompetitorProfileDialogProps {
  competitor?: CompetitorProfile | null;
  features: BusinessFeature[];
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  subsets: CustomerProfileSubset[];
  workspaceSlug: string;
}

export function CompetitorProfileDialog({
  competitor,
  features,
  isOpen,
  onOpenChange,
  onSuccess,
  subsets,
  workspaceSlug,
}: CompetitorProfileDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CompetitorProfileFormData>({
    resolver: zodResolver(competitorProfileSchema),
    defaultValues: emptyCompetitor,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    reset(competitor ? toFormData(competitor) : emptyCompetitor);
  }, [competitor, isOpen, reset]);

  const onSubmit = async (data: CompetitorProfileFormData) => {
    await withLoading(async () => {
      await withToaster(async () => {
        if (competitor) {
          await updateCompetitorProfile(workspaceSlug, competitor.id, data);
          toaster.create({ type: "success", title: "Competitor updated" });
        } else {
          await createCompetitorProfile(workspaceSlug, data);
          toaster.create({ type: "success", title: "Competitor created" });
        }
        onOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {competitor ? "Edit Competitor" : "Create Competitor"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody paddingBottom="6">
            <Stack gap="4">
              <Field
                label="Name"
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  size="sm"
                  placeholder="The competitor's name"
                  {...register("name")}
                />
              </Field>
              <Field
                label="How do they compete with you?"
                invalid={!!errors["competitive-niche"]}
                errorText={errors["competitive-niche"]?.message}
              >
                <Textarea
                  {...register("competitive-niche")}
                  placeholder="Their positioning, and where they overlap with you."
                  rows={2}
                />
              </Field>
              <Field
                label="Website"
                invalid={!!errors["website-url"]}
                errorText={errors["website-url"]?.message}
              >
                <Input
                  {...register("website-url")}
                  placeholder="https://competitor.example.com"
                />
              </Field>
              <Field
                label="Competing features"
                helperText="No text means they compete on all features."
                invalid={!!errors["competing-features"]}
                errorText={errors["competing-features"]?.message}
              >
                <Textarea
                  {...register("competing-features")}
                  placeholder="Name the features they compete on."
                  rows={2}
                />
              </Field>
              <Field
                label="Your differentiators"
                invalid={!!errors.differentiators}
                errorText={errors.differentiators?.message}
              >
                <Textarea
                  {...register("differentiators")}
                  placeholder="What sets you apart from them."
                  rows={2}
                />
              </Field>
              <Field
                label="Where are you stronger?"
                invalid={!!errors["relative-strengths"]}
                errorText={errors["relative-strengths"]?.message}
              >
                <Textarea
                  {...register("relative-strengths")}
                  placeholder="The situations where you come out ahead."
                  rows={2}
                />
              </Field>
              <Field
                label="Where are you weaker?"
                invalid={!!errors["relative-weaknesses"]}
                errorText={errors["relative-weaknesses"]?.message}
              >
                <Textarea
                  {...register("relative-weaknesses")}
                  placeholder="Where an alternative, including them, may be stronger."
                  rows={2}
                />
              </Field>
              {/* The KnowledgeCheckboxGroup helper at the bottom of
                  this file avoids <Field> deliberately: a Chakra Field
                  shares its label/input ids with every control inside
                  it, which makes each checkbox's label point at the
                  first checkbox's input. */}
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Applies to all subsets when none are selected."
                items={subsets}
                label="Applicable ICP subsets"
                name="subset-ids"
              />
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Optional."
                items={features}
                label="Related business features"
                name="related-feature-ids"
              />
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange({ open: false })}
            >
              Cancel
            </Button>
            <Button loading={isLoading} type="submit">
              {competitor ? "Save Changes" : "Create Competitor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

// File-local helper for the id-array checkbox groups. Field is
// deliberately avoided: a Chakra Field shares its label/input ids
// with every control inside it, which makes each checkbox's label
// point at the first checkbox's input.
interface KnowledgeCheckboxGroupProps {
  control: Control<CompetitorProfileFormData>;
  helperText: string;
  items: { id: number; name: string }[];
  label: string;
  name: "subset-ids" | "related-feature-ids";
}

function KnowledgeCheckboxGroup({
  control,
  helperText,
  items,
  label,
  name,
}: KnowledgeCheckboxGroupProps) {
  return (
    <Stack gap="1">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Stack gap="2">
            {items.map((item) => {
              const checked = field.value.includes(item.id);
              return (
                <Checkbox
                  key={item.id}
                  checked={checked}
                  onCheckedChange={(details) => {
                    const next = details.checked
                      ? [...field.value, item.id]
                      : field.value.filter((id) => id !== item.id);
                    field.onChange(next);
                  }}
                >
                  {item.name}
                </Checkbox>
              );
            })}
            <Text color="fg.muted" fontSize="sm" fontStyle="italic">
              {helperText}
            </Text>
          </Stack>
        )}
      />
    </Stack>
  );
}

const emptyCompetitor: CompetitorProfileFormData = {
  name: "",
  "competitive-niche": "",
  "website-url": "",
  "competing-features": "",
  differentiators: "",
  "relative-strengths": "",
  "relative-weaknesses": "",
  "subset-ids": [],
  "related-feature-ids": [],
};

// The schema's nullable fields arrive as null over the wire; empty
// strings are the form's representation of "unset".
const toFormData = (
  competitor: CompetitorProfile,
): CompetitorProfileFormData => ({
  name: competitor.name ?? "",
  "competitive-niche": competitor["competitive-niche"] ?? "",
  "website-url": competitor["website-url"] ?? "",
  "competing-features": competitor["competing-features"] ?? "",
  differentiators: competitor.differentiators ?? "",
  "relative-strengths": competitor["relative-strengths"] ?? "",
  "relative-weaknesses": competitor["relative-weaknesses"] ?? "",
  "subset-ids": competitor["subset-ids"] ?? [],
  "related-feature-ids": competitor["related-feature-ids"] ?? [],
});
